import * as React from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
}

export const FormFieldContext =
  React.createContext<FormFieldContextValue | null>(null);

const useFormFieldIds = (name: string) => {
  return React.useMemo(
    () => ({
      formItemId: `${name}-form-item`,
      formDescriptionId: `${name}-form-description`,
      formMessageId: `${name}-form-message`,
    }),
    [name]
  );
};

export const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const { getFieldState, formState } = useFormContext();

  if (!fieldContext) {
    throw new Error('useFormField must be used within a FormField component');
  }

  const fieldState = getFieldState(fieldContext.name, formState);
  const ids = useFormFieldIds(fieldContext.name);

  return {
    name: fieldContext.name,
    ...ids,
    ...fieldState,
  };
};
